import { useEffect } from "react";
import { Control, Controller, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Spinner } from "src/core/components/custom-ui/spinner";
import { RadioGroup, RadioGroupItem } from "src/core/components/ui/radio-group";
import { cn } from "src/core/lib/utils";
import { EReputationMatchResult } from "src/core/types/entity";
import { WatchlistIconType } from "src/core/types/watchlist";
import { getEntityWatchlistTypes } from "src/core/utils/entity";

type EntityMatchListProps = {
  results: EReputationMatchResult[];
  selectedMatch: EReputationMatchResult | null;
  onSelect: (match: EReputationMatchResult) => void;
  control: Control<Record<string, boolean | undefined>>;
  isLoading: boolean;
  setValue: UseFormSetValue<Record<string, boolean | undefined>>;
  watch: UseFormWatch<Record<string, boolean | undefined>>;
  isReadOnly?: boolean;
};

export const EntityMatchList = ({
  results,
  selectedMatch,
  onSelect,
  control,
  isLoading,
  setValue,
  watch,
  isReadOnly,
}: EntityMatchListProps) => {
  const { t } = useTranslation();

  const labelIconMap: Record<WatchlistIconType, string> = {
    [WatchlistIconType.PEP]: t("modal.entityDetails.eReputation.pep"),
    [WatchlistIconType.SAN_PERSON]: t("modal.entityDetails.eReputation.sanction"),
    [WatchlistIconType.SAN_ENTITY]: t("modal.entityDetails.eReputation.sanction"),
    [WatchlistIconType.SIE]: t("modal.entityDetails.eReputation.sanction"),
  };

  const bgIconMap: Record<WatchlistIconType, string> = {
    [WatchlistIconType.PEP]: "bg-alert",
    [WatchlistIconType.SAN_PERSON]: "bg-danger",
    [WatchlistIconType.SAN_ENTITY]: "bg-danger",
    [WatchlistIconType.SIE]: "bg-danger",
  };

  useEffect(() => {
    setValue("allFalsePositive", undefined);
  }, [setValue]);

  return (
    <>
      <div className="flex flex-row h-30 mb-10 uppercase items-center text-xs">
        <span className="w-5/12">{t("modal.entityDetails.eReputation.match")}</span>
        <span className="w-2/12">{t("modal.entityDetails.eReputation.relevance")}</span>
        <span className="w-4/12 text-center">
          {t("modal.entityDetails.eReputation.identification")}
        </span>
      </div>

      <div className="flex flex-col gap-5 overflow-y-auto">
        {isLoading && <Spinner />}
        {!isLoading &&
          results.map((result) => {
            const watchlistTypes: Set<WatchlistIconType> = getEntityWatchlistTypes(result);

            return (
              <div className="flex flex-row items-center gap-x-5" key={result.id}>
                <div className="w-5/12">
                  <button
                    type="button"
                    onClick={() => onSelect(result)}
                    data-selected={selectedMatch?.id === result.id}
                    className="person-card w-full"
                  >
                    <div className="flex flex-col w-full justify-center">
                      <div className="line-clamp-2">{result.primaryName}</div>
                      <div className="font-light text-xs">
                        {result.dateOfBirth && result.dateOfBirth + " - "}
                        {result.countryName}
                      </div>
                    </div>

                    {watchlistTypes.size > 0 && (
                      <div className="flex items-center justify-center gap-10">
                        {Array.from(watchlistTypes).map((type) => {
                          return (
                            <span
                              key={type}
                              className={cn(
                                "flex rounded-lg border border-border text-center py-5 px-20 text-white",
                                bgIconMap[type] ?? ""
                              )}
                            >
                              {labelIconMap[type] ?? ""}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </button>
                </div>
                <div className="w-2/12 uppercase flex flex-col">
                  <div className="w-[80px] bg-gray-xxlight rounded-lg border border-border flex items-center justify-center">
                    <div className="text-center py-5 px-20">
                      <div>
                        {result.score === 100
                          ? t("modal.entityDetails.eReputation.exactRelevance")
                          : t("modal.entityDetails.eReputation.highRelevance")}
                      </div>

                      <div>{result.score}%</div>
                    </div>
                  </div>
                </div>
                <span className="w-4/12 pl-10">
                  <Controller
                    name={result.id}
                    control={control}
                    render={({ field }) => (
                      <RadioGroup
                        value={
                          field.value === true
                            ? "true"
                            : field.value === false
                            ? "false"
                            : "undefined"
                        }
                        onValueChange={(val) => {
                          if (isReadOnly) return;
                          field.onChange(
                            val === "true" ? true : val === "false" ? false : undefined
                          );
                          if (val === "false") {
                            results.forEach((r) => {
                              if (r.id !== result.id) {
                                setValue(r.id, true);
                              }
                            });
                            setValue("allFalsePositive", undefined);
                          } else if (val === "true") {
                            const allTrue = results.every((r) => {
                              if (r.id === result.id) return true;
                              const currentValue = watch(r.id);
                              return currentValue === true;
                            });
                            if (allTrue) {
                              setValue("allFalsePositive", true);
                            }
                          }
                        }}
                        className="grid grid-cols-2"
                      >
                        <div className="flex items-center space-x-2 font-light">
                          <RadioGroupItem
                            value="true"
                            id={`${result.id}-oui`}
                            disabled={isReadOnly}
                          />
                          <label
                            htmlFor={`${result.id}-oui`}
                            className={`text-sm ${
                              isReadOnly ? "text-muted cursor-not-allowed" : ""
                            }`}
                          >
                            {t("modal.entityDetails.form.falsePositive")}
                          </label>
                        </div>
                        <div className="flex items-center space-x-2 font-light">
                          <RadioGroupItem
                            value="false"
                            id={`${result.id}-non`}
                            disabled={isReadOnly}
                          />
                          <label
                            htmlFor={`${result.id}-non`}
                            className={`text-sm ${
                              isReadOnly ? "text-muted cursor-not-allowed" : ""
                            }`}
                          >
                            {t("modal.entityDetails.form.confirmed")}
                          </label>
                        </div>
                      </RadioGroup>
                    )}
                  />
                </span>
              </div>
            );
          })}
      </div>

      {results.length > 1 && (
        <div className="flex flex-row h-40 mt-20 mb-10 mr-20 pl-[70px] items-center shrink-0 font-light border border-border bg-gray-xxlight">
          <div className="w-7/12 text-right pr-40">
            {t("modal.entityDetails.form.allFalsePositive")}
          </div>
          <div className="w-5/12">
            <Controller
              name="allFalsePositive"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  value={field.value === true ? "true" : "undefined"}
                  onValueChange={(val) => {
                    if (isReadOnly) return;
                    const checked = val === "true" ? true : undefined;
                    field.onChange(checked);

                    if (checked === true) {
                      results.forEach((result) => {
                        setValue(result.id, true);
                      });
                    }
                  }}
                  className="flex items-center space-x-4"
                >
                  <div className="flex space-x-2">
                    <RadioGroupItem value="true" id="all-oui" disabled={isReadOnly} />
                    <label
                      htmlFor="all-oui"
                      className={`text-sm ${isReadOnly ? "text-muted cursor-not-allowed" : ""}`}
                    >
                      {t("modal.entityDetails.form.falsePositive")}
                    </label>
                  </div>
                </RadioGroup>
              )}
            />
          </div>
        </div>
      )}
    </>
  );
};
